/**
 * TdsRowMenu — the three dots at the end of a TDS table row.
 *
 * Shared by the transactions table and the challan table. Two menus that
 * look alike and behave slightly differently is the thing worth avoiding:
 * they sit on the same page under the same tab strip, and a Delete that
 * dismisses on Escape in one and not the other is a bug nobody reports and
 * everybody notices.
 *
 * The list is rendered in a PORTAL on <body>, not beside the button.
 * These tables live inside `overflow: auto` so they can scroll sideways,
 * and an absolutely-positioned child of a scroll container is clipped by
 * it — the menu came out sliced in half with scrollbars appearing around
 * it. A portal has no overflow ancestor to be cut by, which is the same
 * reason FilterDropdown renders its own menu that way.
 *
 * Items are passed in, so a row with nothing to delete can offer only
 * Edit, and one where an action is refused can say why on hover rather
 * than pretending the option is not there.
 */
import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

export const ROW_MENU_CSS = `
.tds-row-dots {
  border: 0; background: none; cursor: pointer; padding: 4px; border-radius: 6px;
  color: var(--gt-text-tertiary); display: inline-flex;
}
.tds-row-dots:hover,
.tds-row-dots[aria-expanded="true"] { background: var(--gt-surface-2); color: var(--gt-text-secondary); }

.tds-row-list {
  position: fixed; z-index: 1200; min-width: 148px;
  background: var(--gt-surface-0);
  border: 1px solid var(--gt-border);
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.16);
  padding: 5px;
}
.tds-row-item {
  display: block; width: 100%; text-align: left; border: 0; background: none;
  padding: 7px 10px; border-radius: 7px; font-size: 12.5px; cursor: pointer;
  color: var(--gt-text-secondary); white-space: nowrap;
}
.tds-row-item:hover:not(:disabled) { background: var(--gt-surface-2); color: var(--gt-text-primary); }
.tds-row-item.is-danger { color: #b91c1c; }
.tds-row-item.is-danger:hover:not(:disabled) { background: #fee2e2; color: #991b1b; }
.tds-row-item:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export interface RowMenuItem {
  label: string;
  onSelect: () => void;
  /** Reddens it. For the one that takes something away. */
  danger?: boolean;
  /** Greyed rather than hidden: an option that is missing reads as "this
   *  cannot be done here", and the reason on hover is the useful answer. */
  disabled?: boolean;
  title?: string;
}

/** Roughly what the list will be, so it can be flipped above the button
 *  when there is no room below rather than hanging off the screen. */
const ITEM_HEIGHT = 31;
const LIST_PADDING = 12;
const GAP = 6;

export default function TdsRowMenu({ items }: { items: RowMenuItem[] }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);

  const close = useCallback(() => setOpen(false), []);

  // Measured from the button on every open, and again on scroll and
  // resize: a fixed element does not move with the page, so without this
  // the menu would stay behind while the table scrolled under it.
  useLayoutEffect(() => {
    if (!open) { setAt(null); return undefined; }
    const place = () => {
      const r = trigger.current?.getBoundingClientRect();
      if (!r) return;
      const height = items.length * ITEM_HEIGHT + LIST_PADDING;
      const below = window.innerHeight - r.bottom - GAP;
      // Above the button when there is not room under it -- a menu
      // hanging off the bottom of the window is one whose last item
      // cannot be clicked.
      const top = below >= height ? r.bottom + GAP : Math.max(GAP, r.top - height - GAP);
      setAt({ top, right: Math.max(GAP, window.innerWidth - r.right) });
    };
    place();
    window.addEventListener("resize", place);
    // Capture, so a scroll in ANY ancestor is caught -- the table's own
    // scroll container does not bubble its scroll event.
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, items.length]);

  // Closed by a click anywhere outside it, or by Escape. The list is in a
  // portal, so "outside" has to mean outside BOTH the button and the list.
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (trigger.current?.contains(target)) return;
      if (list.current?.contains(target)) return;
      close();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open, close]);

  return (
    <>
      <button ref={trigger} type="button" className="tds-row-dots" aria-label="Row actions"
        aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <MoreVertical style={{ width: 16, height: 16 }} />
      </button>

      {open && at && createPortal(
        <div ref={list} className="tds-row-list" role="menu"
          style={{ top: at.top, right: at.right }}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`tds-row-item${item.danger ? " is-danger" : ""}`}
              disabled={item.disabled}
              title={item.title}
              onClick={() => { setOpen(false); item.onSelect(); }}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
